import { put, post, deleteItem } from 'appJS/utils/fetch';
import snakeCaseKeys from 'snakecase-keys';
import { message } from 'antd';
import { useApolloClient } from '@apollo/client';
import { DashboardTask } from './queries';
import {
  DashboardProjectContext,
  DashboardProjectContextData,
  DashboardProjectContextVars,
  GET_DASHBOARD_PROJECT_CONTEXT
} from './projectContextQueries';

type Args = {
  task: DashboardTask;
  projectContext: DashboardProjectContext | null;
  bugherdUrl: string;
  refetchTask: () => void;
  onDeleted: () => void;
};

export const useTaskMutations = ({
  task,
  projectContext,
  bugherdUrl,
  refetchTask,
  onDeleted
}: Args) => {
  const { id: taskId, project } = task;
  const projectId = project.id;
  const projectTags = projectContext?.tags || [];
  const apolloClient = useApolloClient();

  const createTag = (newTagName: string) => {
    const variables: DashboardProjectContextVars = {
      projectId,
      organizationId: project.organizationId,
      apiKey: project.apiKey
    };

    try {
      const cached = apolloClient.readQuery<DashboardProjectContextData>({
        query: GET_DASHBOARD_PROJECT_CONTEXT,
        variables
      });
      if (!cached?.currentUser?.userProject) return;
      if (cached.currentUser.userProject.tags.some(tag => tag.name === newTagName)) return;

      apolloClient.writeQuery<DashboardProjectContextData>({
        query: GET_DASHBOARD_PROJECT_CONTEXT,
        variables,
        data: {
          ...cached,
          currentUser: {
            ...cached.currentUser,
            userProject: {
              ...cached.currentUser.userProject,
              tags: [
                ...cached.currentUser.userProject.tags,
                { id: `new-${newTagName}`, name: newTagName, active: true, taskCount: 0 }
              ]
            }
          }
        }
      });
    } catch {
      // Project context not yet cached; the new tag will appear once the task update resolves.
    }
  };

  const updateTask = (attributes: Record<string, unknown>) =>
    put(
      `${bugherdUrl}/projects/${projectId}/tasks/${taskId}`,
      snakeCaseKeys(attributes, { deep: true })
    )
      .then(() => {
        message.success('Task was successfully updated');
        refetchTask();
      })
      .catch(error => {
        console.error(error);
        message.error('Task was not updated, please try again.');
      });

  const moveTaskToProject = (destinationProjectId: number) =>
    post(`${bugherdUrl}/projects/${projectId}/tasks/move`, {
      id: taskId,
      destination_project_id: destinationProjectId
    })
      .then(({ destination_project_name }) => {
        refetchTask();
        message.success(
          `Task was successfully moved to ${destination_project_name}`
        );
      })
      .catch(error => {
        console.error(error);
        message.error('Task was not moved, please try again.');
      });

  const deleteTask = () => {
    onDeleted();
    deleteItem(`${bugherdUrl}/projects/${projectId}/tasks/${taskId}`)
      .then(() => message.success('Task was successfully deleted'))
      .catch(error => {
        console.error(error);
        message.error('Task was not deleted, please try again.');
      });
  };

  return { updateTask, moveTaskToProject, deleteTask, createTag, projectTags };
};
